// Modelo de pasos del wizard — el equivalente tipado de `applyStepperSkips` del mock
// (surekeep/mockups/_shared/wizard-state.js §153). Fuente única del stepper y de la navegación.
import { hasAccountOpeningMark } from './accountOpening';
import { accountOrigin, alreadyHadAccount } from './accountWait';
import { isSureKeepAvailable } from './sureKeepAvailability';
import type { WizardSession, WizardStepId, WizardStepSlug } from './types';

export interface WizardStepDef {
    id: WizardStepId;
    slug: WizardStepSlug;
    /** Número del paso — va dentro del dot de la píldora del stepper. */
    label: string;
    /** Texto de la píldora del stepper: corto, EXACTO al mock (`wizard/*.html` §stepper). */
    short: string;
    /** Título largo, para encabezados de pantalla. */
    title: string;
    /** Pasos de crédito (pista SOFOM) — se marcan `skipped` en venta de contado. */
    creditOnly: boolean;
}

// Orden del silo: Score (`05`) antes del catálogo; tras el cobro (y la firma) van Entrega, Referidos
// y Bienvenida. El 03 son dos pasos del front, «Elige el teléfono» y «Arma la venta».
export const WIZARD_STEPS: WizardStepDef[] = [
    {
        id: '01',
        slug: 'otp',
        label: '01',
        short: 'Teléfono',
        title: 'Teléfono y términos',
        creditOnly: false,
    },
    {
        id: '02',
        slug: 'kyc',
        label: '02',
        short: 'Identidad',
        title: 'Identidad',
        creditOnly: false,
    },
    {
        id: '05',
        slug: 'score',
        label: '03',
        short: 'Historial',
        title: 'Historial de crédito',
        creditOnly: false,
    },
    {
        id: '03',
        slug: 'product',
        label: '04',
        short: 'Elige el teléfono',
        title: 'Elige el teléfono',
        creditOnly: false,
    },
    {
        id: '03',
        slug: 'build-sale',
        label: '05',
        short: 'Arma la venta',
        title: 'Arma la venta',
        creditOnly: false,
    },
    {
        id: '04',
        slug: 'payment-method',
        label: '06',
        short: 'Pago',
        title: 'Forma de pago',
        creditOnly: false,
    },
    {
        id: '06',
        slug: 'quote',
        label: '07',
        short: 'Cotizar',
        title: 'Cotización',
        creditOnly: true,
    },
    {
        id: '07',
        slug: 'payment',
        label: '08',
        short: 'Cobro',
        title: 'Cobro',
        creditOnly: false,
    },
    {
        id: '08',
        slug: 'sign',
        label: '09',
        short: 'Firma',
        title: 'Firma del contrato',
        creditOnly: true,
    },
    {
        id: '09',
        slug: 'delivery',
        label: '10',
        short: 'Entrega',
        title: 'Entrega',
        creditOnly: false,
    },
    {
        id: '11',
        slug: 'referrals',
        label: '11',
        short: 'Referidos',
        title: 'Referidos',
        creditOnly: false,
    },
    {
        id: '10',
        slug: 'welcome',
        label: '12',
        short: 'Bienvenida',
        title: 'Bienvenida',
        creditOnly: false,
    },
];

// Apertura de cuenta sin venta: el `ACCOUNT_OPENING_PATH_ORDER` del silo (S01, S02, S09, S11, S10).
// Nombres del mock de apertura; `label` es la posición dentro de este camino.
export const ACCOUNT_OPENING_STEPS: WizardStepDef[] = [
    {
        id: '01',
        slug: 'otp',
        label: '01',
        short: 'Identidad',
        title: 'Identidad',
        creditOnly: false,
    },
    {
        id: '02',
        slug: 'kyc',
        label: '02',
        short: 'Verificación',
        title: 'Verificación',
        creditOnly: false,
    },
    {
        id: '09',
        slug: 'delivery',
        label: '03',
        short: 'App y tarjeta',
        title: 'App y tarjeta',
        creditOnly: false,
    },
    {
        id: '11',
        slug: 'referrals',
        label: '04',
        short: 'Referidos',
        title: 'Referidos',
        creditOnly: false,
    },
    {
        id: '10',
        slug: 'welcome',
        label: '05',
        short: 'Bienvenida',
        title: 'Bienvenida',
        creditOnly: false,
    },
];

type PathSession = Partial<Pick<WizardSession, 'accountOpening'>>;

/** Pasos del camino de la sesión: la marca de apertura manda, sin ella es la venta. */
export function stepsFor(session: PathSession | null | undefined): WizardStepDef[] {
    return session && hasAccountOpeningMark(session) ? ACCOUNT_OPENING_STEPS : WIZARD_STEPS;
}

// Score (`05`) NO va aquí: corre siempre, en contado y en crédito (marketing + affordableOnCredit).
/** Pasos de crédito que se brincan en venta de contado (M1_WIZARD §434). */
const CREDIT_STEP_IDS = new Set<WizardStepId>(['06', '08']);

/** Pasos que sobran cuando el comprador ya traía cuenta, en cualquier forma de pago. */
const PRIOR_ACCOUNT_STEP_IDS = new Set<WizardStepId>(['10']);

/**
 * La entrega sobra por cuenta previa solo en contado. En crédito la pantalla sigue teniendo trabajo
 * propio —el IMEI y el candado remoto del equipo financiado—, así que no se tacha.
 */
const PRIOR_ACCOUNT_CASH_STEP_IDS = new Set<WizardStepId>(['09']);

/**
 * Un paso se tacha solo cuando la forma de pago ya se decidió (el 04 quedó atrás): mientras es null
 * la pista sigue abierta. Lo que sobra por cuenta previa se tacha en las DOS pistas.
 */
type StepperSession = Pick<
    WizardSession,
    'paymentMethodKind' | 'kyc' | 'accountStatus' | 'loan' | 'accountOpening'
>;

export function isStepSkipped(step: WizardStepDef, session: StepperSession): boolean {
    // La apertura solo lleva sus cinco pasos y todos tienen trabajo.
    if (hasAccountOpeningMark(session)) return false;
    // Sin nivel SureKeep la pantalla del historial no se muestra.
    if (step.id === '05' && !isSureKeepAvailable(session)) return true;
    if (session.paymentMethodKind === null) return false;
    const isCash = session.paymentMethodKind === 'CASH_OR_CARD';
    if (isCash && CREDIT_STEP_IDS.has(step.id)) return true;
    // Sin app que instalar, sin tarjeta que darle y sin bono N2 que activar sobre una cuenta que ya
    // lo tuvo. La entrega solo sobra en contado: la de crédito además enrola el IMEI y el candado.
    const skippableByAccount =
        PRIOR_ACCOUNT_STEP_IDS.has(step.id) || (isCash && PRIOR_ACCOUNT_CASH_STEP_IDS.has(step.id));
    return skippableByAccount && alreadyHadAccount(accountOrigin(session));
}

/**
 * ¿El paso ya quedó atrás? Lo dice la posición de `currentStep` del silo (`doneIdx`, el primer paso
 * con ese id). Score es la excepción: el silo asienta `S05` tarde, así que manda su evaluación.
 */
export function isStepDone(
    step: WizardStepDef,
    session: Pick<WizardSession, 'loan'>,
    orderIdx: number,
    doneIdx: number,
): boolean {
    if (step.id === '05') return !!session.loan?.sciEvaluationId;
    return orderIdx <= doneIdx;
}

/** Posición del paso en el stepper por su slug; `-1` si no es un paso (el cierre `summary`). */
export function stepIndexBySlug(
    slug: string | undefined,
    steps: readonly WizardStepDef[] = WIZARD_STEPS,
): number {
    return steps.findIndex((s) => s.slug === slug);
}

/** Posición del checkpoint del silo: el primer paso con ese id («Elige el teléfono» en el 03). */
export function stepIndexById(
    id: WizardStepId,
    steps: readonly WizardStepDef[] = WIZARD_STEPS,
): number {
    return steps.findIndex((s) => s.id === id);
}

export function stepBySlug(slug: WizardStepSlug): WizardStepDef | undefined {
    return WIZARD_STEPS.find((s) => s.slug === slug);
}

/** Primer paso con ese id: el 03 resuelve a «Elige el teléfono». */
export function stepById(id: WizardStepId): WizardStepDef | undefined {
    return WIZARD_STEPS.find((s) => s.id === id);
}

/**
 * Slug de destino para el `currentStep`. Una apertura nunca resuelve a un paso de venta: lo que no
 * es de su camino cae en Verificación.
 */
export function slugForStep(
    id: WizardStepId,
    session?: Partial<Pick<WizardSession, 'accountOpening'>>,
): WizardStepSlug {
    if (session && hasAccountOpeningMark(session)) {
        return ACCOUNT_OPENING_STEPS.find((s) => s.id === id)?.slug ?? 'kyc';
    }
    return stepById(id)?.slug ?? 'otp';
}

/** Paso anterior de `slug`, brincando los tachados; `undefined` en el primero. */
export function previousStepSlug(
    slug: WizardStepSlug,
    session: StepperSession,
): WizardStepSlug | undefined {
    const steps = stepsFor(session);
    const index = stepIndexBySlug(slug, steps);
    return steps
        .slice(0, Math.max(index, 0))
        .reverse()
        .find((step) => !isStepSkipped(step, session))?.slug;
}

/**
 * Adónde sigue la venta ya cobrada o firmada. El cursor es el último paso completado: tras Entrega
 * van Referidos, y tras Referidos (guardado u omitido) la Bienvenida.
 */
export function stepAfterCheckout(
    session: Partial<Pick<WizardSession, 'currentStep' | 'accountOpening'>> | null | undefined,
): Extract<WizardStepSlug, 'delivery' | 'referrals' | 'welcome'> {
    const steps = stepsFor(session);
    const cursor = session?.currentStep ? stepIndexById(session.currentStep, steps) : -1;
    if (cursor >= stepIndexById('11', steps)) return 'welcome';
    if (cursor >= stepIndexById('09', steps)) return 'referrals';
    return 'delivery';
}
